<script setup lang="ts">
import { HybridlyFormData } from '@/@types/global';

import { ButtonColor, ButtonVariant } from '@/hooks/useButtonClasses';

import { countryOptions } from '@/utils/countries';
import { stateOptions } from '@/utils/states';

import Button from '@/components/button/Button.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';
import Fieldset from '@/components/fieldset/Fieldset.vue';
import Form from '@/components/form/Form.vue';
import FormCheckboxInput from '@/components/formCheckboxInput/FormCheckboxInput.vue';
import FormComboboxInput from '@/components/formComboboxInput/FormComboboxInput.vue';
import FormFooter from '@/components/formFooter/FormFooter.vue';
import FormHeader from '@/components/formHeader/FormHeader.vue';
import FormInlineFields from '@/components/formInlineFields/FormInlineFields.vue';
import FormListboxInput from '@/components/formListboxInput/FormListboxInput.vue';
import FormRadioInput from '@/components/formRadioInput/FormRadioInput.vue';
import FormTextInput from '@/components/formTextInput/FormTextInput.vue';

type EnumData = App.Base.Data.EnumData;

const props = defineProps<{
  isEditing?: boolean;
  form: HybridlyFormData<{
    name: string;
    program_length: number;
    selectivity: EnumData;
    hbcu: boolean;
    css_required: boolean;
    address: string;
    city: string;
    state: string;
    country: string;
    zip_code: string;
    waiver_fee: number;
    ipeds: string | null;
  }>;
  programLengths: Array<EnumData>;
  selectivities: Array<EnumData>;
  returnUrl?: string;
}>();

const isCancelDialogOpen = ref(false);

const { onBackForward } = useBackForward();

// TODO: Fix intecepting the back button
onBackForward(() => {
  router.abort();
});

function handleToggleCancelDialog() {
  if (props.form.isDirty) {
    isCancelDialogOpen.value = !isCancelDialogOpen.value;
    return;
  }
  handleRedirect();
}

function handleRedirect() {
  router.navigate({ url: props.returnUrl || route('institutes.index') });
}
</script>

<template>
  <Form @submit.prevent="form.submit">
    <template #header>
      <FormHeader
        canClose
        :title="`${isEditing ? 'Edit' : 'Add'} a Institution`"
        :description="
          isEditing
            ? 'Use the form below to edit the institution.'
            : 'Fill out the form below add a new institution.'
        "
        @onClose="handleToggleCancelDialog"
      />
    </template>
    <Fieldset title="General information">
      <FormTextInput
        :error="form.errors.name"
        :modelValue="form.fields.name"
        label="Institution name"
        name="name"
        placeholder="e.g. Riverside High School"
        @update:modelValue="(value) => (form.fields.name = value)"
      />
      <FormListboxInput
        label="Selectivity"
        placeholder="Select selectivity"
        :error="form.errors.selectivity"
        :modelValue="form.fields.selectivity"
        :options="selectivities"
        @update:modelValue="(value) => (form.fields.selectivity = value)"
      />
      <FormRadioInput
        label="Program length"
        :error="form.errors.program_length"
        :isFieldset="false"
        :radioInputs="programLengths"
        :value="form.fields.program_length"
        ariaLabel="Program length"
        name="program_length"
        @update:modelValue="(value) => (form.fields.program_length = value)"
      />
      <FormTextInput
        label="IPEDS Code (optional)"
        :error="form.errors.ipeds"
        :modelValue="form.fields.ipeds"
        name="ipeds"
        @update:modelValue="(value) => (form.fields.ipeds = value)"
      />
    </Fieldset>
    <Fieldset title="Institution address">
      <FormTextInput
        :error="form.errors.address"
        :modelValue="form.fields.address"
        label="Address"
        name="address"
        placeholder="Institution street address and number"
        @update:modelValue="(value) => (form.fields.address = value)"
      />
      <FormComboboxInput
        :error="form.errors.state"
        :modelValue="form.fields.state"
        :options="stateOptions()"
        label="State"
        placeholder="Select state"
        @update:modelValue="(value) => (form.fields.state = value)"
      />
      <FormInlineFields>
        <FormTextInput
          :error="form.errors.city"
          :modelValue="form.fields.city"
          label="City"
          name="city"
          placeholder="Enter city"
          @update:modelValue="(value) => (form.fields.city = value)"
        />
        <FormTextInput
          :error="form.errors.zip_code"
          :modelValue="form.fields.zip_code"
          label="Zip code"
          name="zip_code"
          placeholder="Enter zip code"
          @update:modelValue="(value) => (form.fields.zip_code = value)"
        />
      </FormInlineFields>
      <FormComboboxInput
        :error="form.errors.country"
        :modelValue="form.fields.country"
        :options="countryOptions()"
        label="Country"
        placeholder="Select country"
        @update:modelValue="(value) => (form.fields.country = value)"
      />
    </Fieldset>
    <Fieldset title="Other information">
      <FormCheckboxInput
        :checked="form.fields.hbcu"
        :error="form.errors.hbcu"
        helperText="This institution is recognized as an HBCU."
        label="HBCU"
        name="hbcu"
        @update:checked="(value) => (form.fields.hbcu = value)"
      />
      <FormCheckboxInput
        :checked="form.fields.css_required"
        :error="form.errors.css_required"
        helperText="Short sentence about the purpose of this checkbox."
        label="CSS profile required"
        name="css_required"
        @update:checked="(value) => (form.fields.css_required = value)"
      />
      <FormTextInput
        label="Waiver fee (optional)"
        :error="form.errors.waiver_fee"
        :modelValue="form.fields.waiver_fee"
        name="waiver_fee"
        placeholder="Enter amount"
        prepend="$"
        type="number"
        @update:modelValue="(value) => (form.fields.waiver_fee = value)"
      />
    </Fieldset>
    <template #footer>
      <FormFooter>
        <Button
          type="submit"
          :color="ButtonColor.primary"
          :isDisabled="form.processing || !form.isDirty"
          :isLoading="form.processing"
          :variant="ButtonVariant.solid"
          >Confirm</Button
        >
        <Button
          @click="handleToggleCancelDialog"
          :color="ButtonColor.slate"
          :variant="ButtonVariant.outlined"
          type="button"
        >
          Back to institutions
        </Button>
      </FormFooter>
    </template>
  </Form>
  <DangerDialog
    :title="`Cancel ${isEditing ? 'editing' : 'adding a new'} institution`"
    :message="`Are you sure you want to cancel ${
      isEditing ? 'editing' : 'adding'
    } this institution? All ${isEditing ? 'changes' : 'information'} will be lost.`"
    :isOpen="isCancelDialogOpen"
    cancelButtonLabel="Stay on this page"
    confirmButtonLabel="Go back to institutions"
    @onCancel="handleToggleCancelDialog"
    @onClose="handleToggleCancelDialog"
    @onConfirm="handleRedirect"
  />
</template>
